import {
    alphaNumeric,
    consonants,
    getLengthOfCharacterFormat,
    getValueOfEncodedCharacter,
    randomChar,
    rudeWordReplacements,
    rudeWords,
    SHORT_CODE_FORMAT,
    vowels,
} from "./shortCodesLookup.js";

/**
 * For creating these variable base representations of numbers, we need to extract the next digit. This
 * extracts the numerical representation of that digit, and returns the digit, and the remainder of the seed
 * that still needs encoding.
 *
 * @internal This is an internal utility function not meant to be used outside this module.
 * @param seed - The numeric seed to extract a digit from
 * @param placeValue - The base or number of possible values for this digit position
 * @returns A tuple containing [extractedDigit, remainingSeed]
 */
function _extractDigitFromSeed(seed: number, placeValue: number): [number, number] {
    const value = seed % placeValue;
    seed = Math.floor(seed / placeValue);
    return [value, seed];
}

/**
 * Extracts a formatted digit from a number for use in a shortcode based on a format character and
 * a seed. It returns the character that should be used as a digit, and the remainder of the seed ready
 * to be used to encode the next digit.
 *
 * @internal This is an internal utility function used for short code generation.
 * @param formatChar - The format character (C, V, L, N, etc.) that determines the character set to use
 * @param seed - The numeric seed to extract a digit from
 * @returns A tuple containing [extractedCharacter, remainingSeed]
 */
function _extractNextDigitBasedOnFormat(formatChar: string, seed: number): [string, number] {
    let value: number;
    formatChar = formatChar.toUpperCase();

    if (formatChar === "C") {
        [value, seed] = _extractDigitFromSeed(seed, consonants.length);
        return [consonants[value].toUpperCase(), seed];
    }

    if (formatChar === "V") {
        [value, seed] = _extractDigitFromSeed(seed, vowels.length);
        return [vowels[value].toUpperCase(), seed];
    }

    if (formatChar === "L") {
        [value, seed] = _extractDigitFromSeed(seed, 25);
        return [randomChar[value].toUpperCase(), seed];
    }

    if (formatChar === "N") {
        [value, seed] = _extractDigitFromSeed(seed, 10);
        return [value.toString(), seed];
    }

    if (formatChar === "*") {
        [value, seed] = _extractDigitFromSeed(seed, randomChar.length);
        return [randomChar[value].toUpperCase(), seed];
    }

    if (formatChar === "A") {
        [value, seed] = _extractDigitFromSeed(seed, alphaNumeric.length);
        return [alphaNumeric[value], seed];
    }
    return [formatChar, seed];
}

/**
 * Converts a numeric short code to its string representation according to the defined format.
 * Handles rude word replacement if specified.
 *
 * @param numericShortCode - The numeric value to convert to a short code string
 * @param doRudeWordReplacement - Whether to replace rude words that might appear in the generated code
 * @returns The generated short code string, or undefined if it contains a rude word and replacement is disabled
 */
export function _generateShortCodeStringFromNumericShortCode(numericShortCode: number, doRudeWordReplacement: boolean) {
    let output = "";
    let seed = numericShortCode;

    for (let i = 0; i < SHORT_CODE_FORMAT.length; i++) {
        let newChar: string;
        const fChar = SHORT_CODE_FORMAT.charAt(i);
        [newChar, seed] = _extractNextDigitBasedOnFormat(fChar, seed);
        output += newChar;
    }

    if (doRudeWordReplacement) {
        // rude word filter. Cannot be reversed right now.
        Object.entries(rudeWordReplacements).forEach(([rudeWord, replacement]) => {
            output = output.replace(rudeWord, replacement);
        });
    } else if (rudeWords.some((rudeWord) => output.includes(rudeWord))) {
        return undefined;
    }

    return output;
}

/**
 * Gets the unique number that was used to generate a short code back from the short code.
 * This numeric representation is called the NumericShortCode.
 *
 * Given the format CVC-NNN, this will get you a number between 0 and 1,805,000
 * @param {string} shortCode
 * @returns {number}
 */
export function _generateNumericShortCodeFromShortCodeString(shortCode: string): number {
    /**
     * The short code is essentially a strange representation of a variable base number where the most significant
     * digit is on the rightmost side. So our shortcode might be:
     *
     * ABC
     *
     * Where A is the least significant digit, and C is the most significant digit. Depending on the format, each
     * digit could have a different number of options. So if we use the format CVC (consonant, vowel, consonant) then
     * A has 21 options, B has 5 options, and C has 21 options.
     *
     * Based on the rules of base conversion, to get this into a number we need to multiply each digit by the
     * number of options available for each less significant digit and sum it all together.
     *
     * Since A is the least significant digit, we do nothing, we just add it to our total.
     *
     * Since B is the next most significant, we multiply it by the number of options available for A (21) and
     * add that to our total
     *
     * Since C is the next digit, we multiply it by the number of options available for A (21) and then by the number
     * of options available for B (5) and add that to our total.
     *
     * Then the sum of the 3 above terms is the number that ABC originally encoded.
     */
    const arrayToSum = shortCode.split("").map((char, index) => {
        const numberOfOptions = getLengthOfCharacterFormat(SHORT_CODE_FORMAT[index]);

        // any non-encoding character like a dash has only one option and is not a "real" digit, so
        // we don't need to count it.
        if (numberOfOptions === 1) {
            return 0;
        }

        // all format characters that precede the character we are on.
        const previousCharacters = SHORT_CODE_FORMAT.slice(0, index);

        // the value of this position in this number, the product of all less significant positions
        // named place value because that is the technical term for it
        const placeValue = previousCharacters
            .split("")
            .reduce((sum, formatChar) => sum * getLengthOfCharacterFormat(formatChar), 1);

        // the place value times the digit at this place is the value that needs to be added to the sum
        return placeValue * getValueOfEncodedCharacter(SHORT_CODE_FORMAT[index], char);
    });

    // add together all the terms to get the total unique numeric short code
    const numericShortCode = arrayToSum.reduce((sum, value) => value + sum, 0);

    return numericShortCode;
}
